import React, { useMemo } from "react";
import { useI18n } from "../../hooks/i18n-hooks";
import { useQuery } from "@apollo/client";
import LoadingBoundary from "../../ui_atoms/LoadingBoundary";
import ListGroup from "../../components/ListGroup";
import { StackScreenProps } from "@react-navigation/stack";
import EmptyScreen from "../../components/EmptyScreen";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { FIND_CLIENT_INFO } from "../../graphql/core";
import NoClientsImage from "../../assets/images/no-clients-image.svg";
import { useDynamicHeaderButton } from "../../hooks/use-dynamic-header-button";
import ScreenCard from "../ScreenCard";
import Button from "../../components/Button";
import { useTheme } from "../../hooks/use-theme";
import { useIsScreenSizeMin } from "../../hooks/useIsScreenSizeMin";
import { StyleSheet } from "react-native";

type RootParams = StackScreenProps<RootStackParamList>;

const WorkerClientListScreen: React.FC<RootParams> = ({ navigation }) => {
    const i18n = useI18n();
    const styles = useStyles();
    const { loading, error, data, refetch } = useQuery(FIND_CLIENT_INFO, {
        fetchPolicy: "cache-and-network",
        variables: {
            query: "",
        },
    });

    const clientInfoList = useMemo(
        () => data?.findClientInfo.elements || [],
        [data?.findClientInfo],
    );

    const items = useMemo(() => {
        const nonSortedItems = clientInfoList.map((clientInfo) => {
            return {
                value: clientInfo.id,
                title: clientInfo.name,
                avatarPath: clientInfo.logoUrl,
                useBigAvatarOrIcon: true,
                description: clientInfo.isPrivateClient ? undefined : clientInfo.profileName,
                labels: [
                    {
                        text: clientInfo.isPrivateClient
                            ? i18n.t("screens.client_profile.tabs.employees.private_client")
                            : undefined,
                    },
                ],
                onPress: () => {
                    navigation.navigate("ClientDetails", {
                        clientProfileId: clientInfo.id,
                    });
                },
            };
        });
        return nonSortedItems.sort((a, b) => a.title.localeCompare(b.title));
    }, [clientInfoList, navigation, i18n]);

    const onInviteClient = React.useCallback(() => {
        navigation.navigate("InviteClientInfo");
    }, [navigation]);

    const dynamicButtonProps = useDynamicHeaderButton(
        items.length > 0
            ? {
                  title: i18n.t("screens.user.missions.invite_client"),
                  shortTitle: i18n.t("screens.user.missions.invite_client_short"),
                  onClick: onInviteClient,
                  icon: "add",
              }
            : undefined,
    );

    return (
        <ScreenCard
            refresh={refetch}
            isContentContainerFlex={items.length === 0}
            {...dynamicButtonProps}
        >
            <LoadingBoundary
                error={error}
                loading={loading && !data}
                clearError={() => void refetch()}
            >
                {items.length == 0 ? (
                    <>
                        <EmptyScreen
                            image={NoClientsImage}
                            text={i18n.t("screens.client_profile.tabs.employees.no_clients")}
                        />
                        <Button
                            size="large"
                            testID="button-invite-client"
                            buttonMode="secondary"
                            style={styles.startButton}
                            onPress={onInviteClient}
                            label={i18n.t("screens.user.missions.invite_client")}
                        />
                    </>
                ) : (
                    <ListGroup isItemsCheckable={false} items={items} alignment="stretch" />
                )}
            </LoadingBoundary>
        </ScreenCard>
    );
};

const useStyles = () => {
    const { gridSize } = useTheme();
    const isLarge = useIsScreenSizeMin("large");

    return useMemo(
        () =>
            StyleSheet.create({
                startButton: {
                    marginHorizontal: gridSize,
                    alignSelf: !isLarge ? "stretch" : "center",
                },
            }),
        [gridSize, isLarge],
    );
};

export default WorkerClientListScreen;
